import AppBackButton from '@nxsys/components/AppBackButton';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppSidebarTabLayout from '@nxsys/components/AppSidebarTabLayout';
import { UMBRELLA_PAYE } from '@nxsys/constants/AppConst';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import {
  useGetCompanyById,
  useUpdateCompanyById,
} from '@nxsys/hooks/api/company';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { showMessage } from '@nxsys/utils/message';
import { Space, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import ActivityLogTab from './ActivityLogTab';
import BankTab from './BankTab';
import BannerTab from './BannerTab';
import ClientApplicationTab from './ClientApplicationTab';
import CompanyDefaultTab from './CompanyDefaultTab';
import CompanyDetailTab from './CompanyDetailTab';
import ContactDetailTab from './ContactDetailTab';
import DepartmentTab from './DepartmentTab';
import EmailTab from './EmailTab';
import ExpenseTab from './ExpenseTab';
import HMRCTab from './HMRCTab';
import IntegrationTab from './IntegrationTab';
import InvoiceSetting from './InvoiceSetting';
import PaymentModelTab from './PaymentModelTab';
import PayrollSettingTab from './PayrollSettingTab';
import PensionIrelandTab from './PensionIrelandTab';
import PensionTab from './PensionTab';
import RevenueSetting from './RevenueSetting';
import SettingTab from './SettingTab';
import WorkingPatternTab from './WorkingPatternTab';
import ZohoSettingsTab from './ZohoSettingsTab';
import { companyTabForCountry } from './company-tab-access';

enum CompanyTabKey {
  COMPANY_DETAIL = 'COMPANY_DETAIL',
  CONTACT_DETAIL = 'CONTACT_DETAIL',
  PAYMENT_MODEL = 'PAYMENT_MODEL',
  EXPENSE = 'EXPENSE',
  SETTING = 'SETTING',
  PENSION = 'PENSION',
  BANK = 'BANK',
  COMPANY_DEFAULT = 'COMPANY_DEFAULT',
  HMRC = 'HMRC',
  INTEGRATION = 'INTEGRATION',
  EMAIL = 'EMAIL',
  CLIENT_APPLICATION = 'CLIENT_APPLICATION',
  ACTIVITY_LOG = 'ACTIVITY_LOG',
  BANNER = 'BANNER',

  PAYROLL_SETTING = 'PAYROLL_SETTING',
  WORKING_PATTERN = 'WORKING_PATTERN',
  DEPARTMENT = 'DEPARTMENT',
  INVOICE_SETTING = 'INVOICE_SETTING',
  REVENUE_SETTING = 'REVENUE_SETTING',
  ZOHO_SETTINGS = 'ZOHO_SETTINGS',
}

const CompanyDetail = () => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const companyId = params?.companyId ?? null;
  const navigate = useNavigateWithQuery();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const { isBureau, payrollCountry, isAdmin } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const {
    run: fetchCompany,
    value: companyValue,
    loading: isFetchingCompany,
  } = useGetCompanyById();
  const companyData = useMemo(() => companyValue?.data, [companyValue]);
  const isIncludeUmbrellaOrPaye = useMemo(() => {
    return UMBRELLA_PAYE.some((model) =>
      companyData?.paymentModel?.includes(model),
    );
  }, [companyData]);

  const isIncludeUmbrellaOrHybrid = useMemo(() => {
    return (
      companyData?.paymentModel?.includes(PAYMENT_MODEL_ENUM.UMBRELLA) ||
      companyData?.paymentModel?.includes(PAYMENT_MODEL_ENUM.HYBRID)
    );
  }, [companyData]);

  useEffect(() => {
    if (companyId) fetchCompany(companyId);
  }, [companyId]);

  const tabs = [
    {
      label: isBureau
        ? messages['common.bureau.companyDetails']
        : messages['common.companyDetails'],
      key: CompanyTabKey.COMPANY_DETAIL,
    },
    {
      label: messages['common.contactDetails'],
      key: CompanyTabKey.CONTACT_DETAIL,
    },
    ...(isAdmin
      ? [
          {
            label: messages['common.paymentModel'],
            key: CompanyTabKey.PAYMENT_MODEL,
          },
        ]
      : []),
    ...(isIncludeUmbrellaOrHybrid
      ? [
          {
            label: messages['common.expense'],
            key: CompanyTabKey.EXPENSE,
          },
        ]
      : []),
    {
      label: messages['common.settings'],
      key: CompanyTabKey.SETTING,
    },
    ...(isBureau
      ? [
          {
            label: messages['common.payrollSetting'],
            key: CompanyTabKey.PAYROLL_SETTING,
          },
          {
            label: messages['common.workingPattern'],
            key: CompanyTabKey.WORKING_PATTERN,
          },
        ]
      : []),
    ...(isBureau || isIncludeUmbrellaOrPaye
      ? [
          {
            label: messages['common.pension'],
            key: CompanyTabKey.PENSION,
          },
        ]
      : []),
    {
      label: messages['common.bankAccount'],
      key: CompanyTabKey.BANK,
    },
    {
      label: isBureau
        ? messages['common.bureau.companyDefault']
        : messages['common.companyDefault'],
      key: CompanyTabKey.COMPANY_DEFAULT,
    },
    ...(isIreland
      ? [
          {
            label: messages['common.revenueSettings'],
            key: CompanyTabKey.REVENUE_SETTING,
          },
        ]
      : [
          {
            label: messages['common.hmrcSettings'],
            key: CompanyTabKey.HMRC,
          },
        ]),
    {
      // NX-5235: bureau gets this tab too, holding Xero Settings alone. The
      // journal is a bureau feature, so hiding Integration from bureau left
      // the nominal-code mapping unreachable for the companies that need it.
      label: messages['common.integration'],
      key: CompanyTabKey.INTEGRATION,
    },
    ...(isBureau && companyData?.subOnboarding
      ? [
          {
            label: messages['common.zohoSettings'],
            key: CompanyTabKey.ZOHO_SETTINGS,
          },
        ]
      : []),
    {
      label: messages['company.emailSettingsAndTemplates.title'],
      key: CompanyTabKey.EMAIL,
    },
    ...(isBureau && isAdmin
      ? [
          {
            label: messages['common.invoiceSettings'],
            key: CompanyTabKey.INVOICE_SETTING,
          },
        ]
      : []),
    {
      label: messages['common.department'],
      key: CompanyTabKey.DEPARTMENT,
    },
    {
      label: messages['company.clientApplication.title'],
      key: CompanyTabKey.CLIENT_APPLICATION,
    },
    {
      label: messages['common.activityLog'],
      key: CompanyTabKey.ACTIVITY_LOG,
    },
    {
      label: messages['company.banner.title'],
      key: CompanyTabKey.BANNER,
    },
  ];

  const [activeTab, setActiveTab] = useState<CompanyTabKey>(
    CompanyTabKey.COMPANY_DETAIL,
  );

  useEffect(() => {
    if (tabParam) {
      const allowedTab = companyTabForCountry(tabParam, isIreland);
      if (allowedTab !== tabParam) {
        navigate(`/companies/${companyId}`, {
          query: { tab: allowedTab },
          replace: true,
        });
      }
      setActiveTab(allowedTab as CompanyTabKey);
    }
  }, [tabParam, isIreland, companyId, navigate]);

  const { run: runUpdateCompany } = useUpdateCompanyById();

  const updateCompany = useCallback(
    async (params: UpdateCompanyByIdParams) => {
      const res = await runUpdateCompany(companyId ?? '', params);
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.updateSuccess'],
        });
        // The settings were saved; HMRC just refused the credentials in them.
        // Long enough to read, since nothing else reports it.
        if (res.warning) {
          showMessage({
            type: 'warning',
            content: res.warning,
            duration: 10,
          });
        }
        fetchCompany(companyId ?? '');
      }
    },
    [companyId, runUpdateCompany],
  );

  const renderTabContent = useCallback(
    (key: CompanyTabKey) => {
      switch (companyTabForCountry(key, isIreland) as CompanyTabKey) {
        case CompanyTabKey.COMPANY_DETAIL:
          return (
            <CompanyDetailTab data={companyData} updateData={updateCompany} />
          );
        case CompanyTabKey.CONTACT_DETAIL:
          return (
            <ContactDetailTab data={companyData} updateData={updateCompany} />
          );
        case CompanyTabKey.PAYMENT_MODEL:
          return (
            <PaymentModelTab data={companyData} updateData={updateCompany} />
          );
        case CompanyTabKey.EXPENSE:
          return <ExpenseTab data={companyData} />;
        case CompanyTabKey.SETTING:
          return <SettingTab data={companyData} updateData={updateCompany} />;
        case CompanyTabKey.PENSION:
          return isIreland ? (
            <PensionIrelandTab
              data={companyData}
              updateCompany={updateCompany}
            />
          ) : (
            <PensionTab
              data={companyData}
              updateData={updateCompany}
              fetchCompany={() => fetchCompany(companyId ?? '')}
            />
          );
        case CompanyTabKey.BANK:
          return (
            <BankTab
              data={companyData}
              fetchCompany={() => fetchCompany(companyId ?? '')}
            />
          );
        case CompanyTabKey.COMPANY_DEFAULT:
          return (
            <CompanyDefaultTab data={companyData} updateData={updateCompany} />
          );
        case CompanyTabKey.HMRC:
          return <HMRCTab data={companyData} updateData={updateCompany} />;
        case CompanyTabKey.REVENUE_SETTING:
          return (
            <RevenueSetting data={companyData} updateData={updateCompany} />
          );
        case CompanyTabKey.INTEGRATION:
          return (
            <IntegrationTab
              data={companyData}
              companyId={companyId ?? undefined}
              isBureau={isBureau}
              fetchCompany={() => fetchCompany(companyId ?? '')}
            />
          );
        case CompanyTabKey.EMAIL:
          return <EmailTab data={companyData} updateData={updateCompany} />;
        case CompanyTabKey.CLIENT_APPLICATION:
          return <ClientApplicationTab data={companyData} />;
        case CompanyTabKey.ACTIVITY_LOG:
          return <ActivityLogTab data={companyData} />;
        case CompanyTabKey.BANNER:
          return <BannerTab data={companyData} />;
        case CompanyTabKey.PAYROLL_SETTING:
          return (
            <PayrollSettingTab data={companyData} updateData={updateCompany} />
          );
        case CompanyTabKey.WORKING_PATTERN:
          return <WorkingPatternTab data={companyData} />;
        case CompanyTabKey.DEPARTMENT:
          return (
            <DepartmentTab data={companyData} updateCompany={updateCompany} />
          );
        case CompanyTabKey.INVOICE_SETTING:
          return (
            <InvoiceSetting data={companyData} updateData={updateCompany} />
          );
        case CompanyTabKey.ZOHO_SETTINGS:
          return (
            <ZohoSettingsTab
              data={companyData}
              companyId={companyId ?? undefined}
            />
          );
        default:
          return <></>;
      }
    },
    [companyData, isIreland, updateCompany],
  );

  const renderTitle = useCallback(
    () => (
      <Space>
        <AppBackButton
          onClick={() => navigate('/companies', { excludeParams: ['tab'] })}
        />
        <Space direction="vertical" size={6}>
          <Typography.Title level={4} className="mb-0">
            {companyData?.companyName ?? ''}
          </Typography.Title>
          <div className="flex items-center gap-x-2">
            <Typography.Text className="font-sm text-gray-500 border-solid border-gray-200 border-r pr-2">{`ID: ${companyData?.id ?? ''}`}</Typography.Text>
            <Typography.Text className="font-sm text-gray-500">{`${messages['common.updatedOn']}: ${moment(companyData?.updatedAt).format('DD/MM/YYYY')}`}</Typography.Text>
          </div>
        </Space>
      </Space>
    ),
    [companyData],
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      loading={isFetchingCompany}
    >
      <AppSidebarTabLayout
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key) => {
          navigate(`/companies/${companyId}`, {
            query: {
              tab: key,
            },
          });
        }}
        contentClassName={
          [
            CompanyTabKey.EXPENSE,
            CompanyTabKey.BANK,
            CompanyTabKey.ACTIVITY_LOG,
            CompanyTabKey.BANNER,
            CompanyTabKey.WORKING_PATTERN,
            CompanyTabKey.DEPARTMENT,
            CompanyTabKey.PENSION,
            CompanyTabKey.REVENUE_SETTING,
            CompanyTabKey.EMAIL,
            CompanyTabKey.CLIENT_APPLICATION,
            CompanyTabKey.INVOICE_SETTING,
            CompanyTabKey.HMRC,
            CompanyTabKey.INTEGRATION,
            CompanyTabKey.ZOHO_SETTINGS,
          ].includes(activeTab)
            ? 'px-6'
            : 'max-w-[472px]'
        }
      >
        <Typography.Title
          level={5}
          className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
        >
          {tabs.find((t) => t.key === activeTab)?.label ?? ''}
        </Typography.Title>
        {renderTabContent(activeTab)}
      </AppSidebarTabLayout>
    </AppsContainer>
  );
};

export default CompanyDetail;
